/**
 * @file PlanRegistry.jsx
 * @module ui/src/components/admin/PlanRegistry
 * @description
 *   CA-1 plan registry shell. Owns all data fetching for the registry surface:
 *   ep 1 GET /api/v1/plans/registry (plan list + keyset pagination),
 *   registry summary (outside v4 scope), saved views (outside v4 scope),
 *   and reference data (fiscal periods, roles, segments — outside v4 scope).
 *   Distributes fetched data to C-02 PlanRegistryToolbar, C-03 PlanAttentionStrip,
 *   and C-04 PlanListRow as props.
 *
 * @version 1.1.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S777
 *
 * @architecture
 *   Locked decisions : #1
 *   Temporal invariants : INV-3
 *   Design principles : DP-5
 *
 * @wiring-s777
 *   ep 1 GET /api/v1/plans/registry — WIRED S777.
 *   Response: {plans[], pagination{hasMore, nextCursor{updatedAt, planId}}}
 *   FIXED S777: nextCursor is nested inside pagination{} — not top-level.
 *   FIXED S777: load-more cursor params are cursor_updated_at + cursor_plan_id (not single 'cursor').
 *   Outside v4 scope (no action): registry/saved-views, registry/summary,
 *     fiscal-periods, roles, segments.
 *
 * @changelog
 *   1.0.0  S756  Initial build
 *   1.1.0  S777  Added JSDoc header. Fixed ep 1 pagination cursor read + send. CRLF→LF.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import PlanAttentionStrip from './PlanAttentionStrip';
import PlanListRow from './PlanListRow';
import PlanRegistryToolbar from './PlanRegistryToolbar';
import NewPlanCreationSheet from './NewPlanCreationSheet';
import PlanSegmentGroup from './PlanSegmentGroup';

export const DEFAULT_FILTERS = {
  fiscalPeriodId: null,
  planStatus: [],
  temporalRelationship: [],
  roleIds: [],
  segmentIds: [],
  buIds: [],
  primarySpine: [],
  attentionLevel: [],
  hasOpenAmendment: null,
  sourceType: [],
  searchQuery: '',
  sortBy: 'updated_at',
  sortDir: 'desc',
};

function filtersToParams(filters) {
  const params = new URLSearchParams();

  if (filters.fiscalPeriodId) {
    params.set(
      'fiscal_period_id',
      String(filters.fiscalPeriodId),
    );
  }

  if (filters.searchQuery) {
    params.set('q', filters.searchQuery);
  }

  if (filters.sortBy) {
    params.set('sort_by', filters.sortBy);
  }

  if (filters.sortDir) {
    params.set('sort_dir', filters.sortDir);
  }

  filters.planStatus.forEach((status) => {
    params.append('plan_status', status);
  });

  filters.temporalRelationship.forEach((relationship) => {
    params.append('temporal', relationship);
  });

  filters.roleIds.forEach((id) => {
    params.append('role_id', String(id));
  });

  filters.segmentIds.forEach((id) => {
    params.append('segment_id', String(id));
  });

  filters.buIds.forEach((id) => {
    params.append('bu_id', String(id));
  });

  filters.primarySpine.forEach((spine) => {
    params.append('spine', spine);
  });

  filters.attentionLevel.forEach((level) => {
    params.append('attention', level);
  });

  filters.sourceType.forEach((source) => {
    params.append('source_type', source);
  });

  if (filters.hasOpenAmendment !== null) {
    params.set(
      'has_open_amendment',
      String(filters.hasOpenAmendment),
    );
  }

  return params;
}

function normalizeSavedView(view) {
  return {
    id: String(view.id ?? view.saved_view_id),
    label: view.label,
    filters: {
      ...DEFAULT_FILTERS,
      ...(view.filters || {}),
    },
  };
}

function hasRegistryFilters(filters) {
  return (
    filters.planStatus.length > 0 ||
    filters.temporalRelationship.length > 0 ||
    filters.roleIds.length > 0 ||
    filters.segmentIds.length > 0 ||
    filters.buIds.length > 0 ||
    filters.primarySpine.length > 0 ||
    filters.attentionLevel.length > 0 ||
    filters.hasOpenAmendment !== null ||
    filters.sourceType.length > 0 ||
    Boolean(filters.searchQuery)
  );
}

/**
 * Top-level server-driven plan registry.
 *
 * Registry rows are summary DTOs only. All searching, filtering, sorting,
 * quick-filter aggregation, and pagination are executed by the server.
 *
 * @param {{
 *   currentUser: { user_account_id: string, [key: string]: any },
 *   fiscalPeriodId: number,
 *   onPlanOpen: (planId: number) => void,
 *   className?: string
 * }} props
 */
export default function PlanRegistry({
  currentUser,
  fiscalPeriodId,
  onPlanOpen,
  className = '',
}) {
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    fiscalPeriodId,
  }));

  const [plans, setPlans] = useState([]);
  // S800: totalCount read from main registry response (registryPayload.totalCount).
  // Parallel fetch to /registry/summary removed — that endpoint does not exist
  // and totalCount is now part of the main ep 1 response envelope.
  // S802: registrySummary captures full registrySummary from ep 1 envelope
  // (OI-S801-ATTENTION-STRIP-SUBCOUNTS closed). PlanAttentionStrip receives
  // the full object — all 7 subcounts now populated from the router.
  const [totalCount, setTotalCount] = useState(null);
  const [registrySummary, setRegistrySummary] = useState(null);
  const [savedViews, setSavedViews] = useState([]);
  const [fiscalPeriods, setFiscalPeriods] = useState([]);
  const [roles, setRoles] = useState([]);
  const [segments, setSegments] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [showCreationSheet, setShowCreationSheet] =
    useState(false);
  const [selectedPlanId, setSelectedPlanId] =
    useState(null);

  const mountedRef = useRef(true);
  const debounceRef = useRef(null);
  const registryAbortRef = useRef(null);
  const loadMoreAbortRef = useRef(null);
  const registryRequestRef = useRef(0);
  const previousSearchRef = useRef(filters.searchQuery);
  // S800: sentinel div at bottom of list — IntersectionObserver fires
  // handleLoadMore automatically when user scrolls to bottom of container.
  // Replaces manual "load more" button.
  const sentinelRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setFilters((current) => ({
      ...current,
      fiscalPeriodId,
    }));
    setNextCursor(null);
  }, [fiscalPeriodId]);

  const fetchSavedViews = useCallback(async () => {
    const response = await fetch(
      '/api/v1/plans/registry/saved-views',
      {
        credentials: 'include',
      },
    );

    if (!mountedRef.current) return;

    if (!response.ok) {
      throw new Error(
        `Unable to load saved views (${response.status}).`,
      );
    }

    const payload = await response.json();

    if (!mountedRef.current) return;

    const rawViews = Array.isArray(payload)
      ? payload
      : payload.savedViews ??
        payload.saved_views ??
        [];

    if (!mountedRef.current) return;

    setSavedViews(rawViews.map(normalizeSavedView));
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadReferenceData() {
      try {
        const savedViewsResponse = await fetch(
          '/api/v1/plans/registry/saved-views',
          {
            credentials: 'include',
          },
        );

        if (!mountedRef.current || cancelled) return;

        if (!savedViewsResponse.ok) {
          throw new Error(
            `Unable to load saved views (${savedViewsResponse.status}).`,
          );
        }

        const savedViewsPayload =
          await savedViewsResponse.json();

        if (!mountedRef.current || cancelled) return;

        const rawViews = Array.isArray(savedViewsPayload)
          ? savedViewsPayload
          : savedViewsPayload.savedViews ??
            savedViewsPayload.saved_views ??
            [];

        if (!mountedRef.current || cancelled) return;

        setSavedViews(rawViews.map(normalizeSavedView));

        const periodsResponse = await fetch(
          '/api/v1/fiscal-periods',
          {
            credentials: 'include',
          },
        );

        if (!mountedRef.current || cancelled) return;

        if (!periodsResponse.ok) {
          throw new Error(
            `Unable to load fiscal periods (${periodsResponse.status}).`,
          );
        }

        const periodsPayload = await periodsResponse.json();

        if (!mountedRef.current || cancelled) return;

        const rawPeriods = Array.isArray(periodsPayload)
          ? periodsPayload
          : periodsPayload.data ??
            periodsPayload.fiscalPeriods ??
            periodsPayload.fiscal_periods ??
            periodsPayload.items ??
            [];

        if (!mountedRef.current || cancelled) return;

        setFiscalPeriods(
          rawPeriods.map((period) => ({
            id:
              period.id ??
              period.fiscalPeriodId ??
              period.fiscal_period_id,
            label:
              period.label ??
              period.periodLabel ??
              period.period_label,
          })),
        );

        const rolesResponse = await fetch(
          '/api/v1/roles?is_active=true',
          {
            credentials: 'include',
          },
        );

        if (!mountedRef.current || cancelled) return;

        if (!rolesResponse.ok) {
          throw new Error(
            `Unable to load roles (${rolesResponse.status}).`,
          );
        }

        const rolesPayload = await rolesResponse.json();

        if (!mountedRef.current || cancelled) return;

        const rawRoles = Array.isArray(rolesPayload)
          ? rolesPayload
          : rolesPayload.data ??
            rolesPayload.roles ??
            rolesPayload.items ??
            [];

        if (!mountedRef.current || cancelled) return;

        setRoles(
          rawRoles.map((role) => ({
            id: role.id ?? role.roleId ?? role.role_id,
            name:
              role.name ??
              role.roleName ??
              role.role_name,
            category: role.role_category ?? role.category ?? null,
          })),
        );

        const segmentsResponse = await fetch(
          '/api/v1/segments?is_active=true',
          {
            credentials: 'include',
          },
        );

        if (!mountedRef.current || cancelled) return;

        if (!segmentsResponse.ok) {
          throw new Error(
            `Unable to load segments (${segmentsResponse.status}).`,
          );
        }

        const segmentsPayload =
          await segmentsResponse.json();

        if (!mountedRef.current || cancelled) return;

        const rawSegments = Array.isArray(segmentsPayload)
          ? segmentsPayload
          : segmentsPayload.data ??
            segmentsPayload.segments ??
            segmentsPayload.items ??
            [];

        if (!mountedRef.current || cancelled) return;

        setSegments(
          rawSegments.map((segment) => ({
            id:
              segment.id ??
              segment.segmentId ??
              segment.segment_id,
            name:
              segment.name ??
              segment.segmentName ??
              segment.segment_name,
          })),
        );
      } catch (requestError) {
        if (!mountedRef.current || cancelled) return;

        setError(
          requestError.message ||
            'Unable to load plan registry reference data.',
        );
      }
    }

    loadReferenceData();

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchRegistry = useCallback(
    async (filterSnapshot) => {
      registryAbortRef.current?.abort();

      const controller = new AbortController();
      registryAbortRef.current = controller;

      const requestId = registryRequestRef.current + 1;
      registryRequestRef.current = requestId;

      setLoading(true);
      setError(null);

      const params = filtersToParams(filterSnapshot);
      params.set('limit', '50');

      try {
        // S800: single fetch only — totalCount is now in the main registry
        // response envelope. /registry/summary endpoint does not exist.
        const registryResponse = await fetch(
          `/api/v1/plans/registry?${params.toString()}`,
          {
            credentials: 'include',
            signal: controller.signal,
          },
        );

        if (!mountedRef.current) return;
        if (controller.signal.aborted) return;
        if (requestId !== registryRequestRef.current) return;

        if (!registryResponse.ok) {
          throw new Error(
            `Unable to load plans (${registryResponse.status}).`,
          );
        }

        const registryPayload = await registryResponse.json();

        if (!mountedRef.current) return;
        if (controller.signal.aborted) return;
        if (requestId !== registryRequestRef.current) return;

        setPlans(registryPayload.plans || []);
        // WHY: ep 1 router wraps cursor in pagination{nextCursor{updatedAt,planId}}.
        // Not top-level nextCursor. Router confirmed S800.
        setNextCursor(
          registryPayload.pagination?.nextCursor ?? null,
        );
        // S800: totalCount from main response envelope (ep 1 enrichment S800).
        setTotalCount(registryPayload.totalCount ?? null);
        // S802: capture full registrySummary — OI-S801-ATTENTION-STRIP-SUBCOUNTS closed.
        // registrySummary contains all 7 subcounts from the router.
        setRegistrySummary(registryPayload.registrySummary ?? null);
      } catch (requestError) {
        if (!mountedRef.current) return;

        if (requestError.name === 'AbortError') return;

        if (requestId !== registryRequestRef.current) {
          return;
        }

        setError(
          requestError.message ||
            'Unable to load compensation plans.',
        );
        setPlans([]);
        setTotalCount(null);
        setNextCursor(null);
      } finally {
        if (!mountedRef.current) return;
        if (requestId !== registryRequestRef.current) return;

        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const searchChanged =
      previousSearchRef.current !== filters.searchQuery;

    previousSearchRef.current = filters.searchQuery;

    if (searchChanged) {
      debounceRef.current = setTimeout(() => {
        fetchRegistry(filters);
      }, 300);
    } else {
      fetchRegistry(filters);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [filters, fetchRegistry]);

  useEffect(() => {
    return () => {
      registryAbortRef.current?.abort();
      loadMoreAbortRef.current?.abort();

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // S800: IntersectionObserver — auto-loads next page when sentinel div
  // at bottom of the list scrolls into view. Replaces manual "load more"
  // button. Fires handleLoadMore only when nextCursor exists and not
  // already loading more. UI-TABLE-01 rule: no endless vertical growth,
  // pagination is seamless via scroll.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && nextCursor && !loadingMore) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [nextCursor, loadingMore]);

  function handleFilterChange(updates) {
    setFilters((current) => ({
      ...current,
      ...updates,
    }));

    setNextCursor(null);
  }

  function handleClearFilters() {
    setFilters((current) => ({
      ...DEFAULT_FILTERS,
      fiscalPeriodId: current.fiscalPeriodId,
    }));

    setNextCursor(null);
  }

  function handlePlanOpen(planId) {
    setSelectedPlanId(planId);
    onPlanOpen(planId);
  }

  function handleSaveAs(planId) {
    /*
     * Batch 3 explicitly leaves SaveAsNewPlanDialog outside this
     * component set. The parent shell can listen for this semantic
     * integration event and open the separately owned dialog without
     * adding an undeclared prop to C-01.
     */
    window.dispatchEvent(
      new CustomEvent(
        'decisionflow:save-as-new-plan',
        {
          detail: { planId },
        },
      ),
    );
  }

  async function handleWithdraw(planId) {
    // Append-only: no DELETE SQL anywhere in this system.
    // Withdraw transitions plan_status → 'withdrawn' (governed state change).
    // ep 11 POST /api/v1/plans/{id}/withdraw — plan_governance_router.py
    // Only valid for pending_approval plans. Draft plans use handleDiscard.
    const response = await fetch(`/api/v1/plans/${planId}/withdraw`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg = typeof errBody.detail === 'string'
        ? errBody.detail
        : errBody.error?.message ?? errBody.error?.code
          ?? `Withdraw failed (${response.status}).`;
      throw new Error(msg);
    }
    // Refresh registry so withdrawn plan reflects new status immediately
    fetchRegistry(filters);
  }

  async function handleDiscard(planId) {
    // S913: Discard transitions plan_status → 'discarded' (draft plans only).
    // Append-only: no DELETE SQL anywhere in this system.
    // POST /api/v1/plans/{id}/discard — plan_governance_router.py
    const response = await fetch(`/api/v1/plans/${planId}/discard`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg = typeof errBody.detail === 'string'
        ? errBody.detail
        : errBody.error?.message ?? errBody.error?.code
          ?? `Discard failed (${response.status}).`;
      throw new Error(msg);
    }
    // Refresh registry so discarded plan disappears from active view immediately
    fetchRegistry(filters);
  }

  async function handleLoadMore() {
    if (!nextCursor || loadingMore) return;

    loadMoreAbortRef.current?.abort();

    const controller = new AbortController();
    loadMoreAbortRef.current = controller;

    setLoadingMore(true);
    setError(null);

    const params = filtersToParams(filters);
    // WHY: ep 1 router uses keyset pagination with two params: cursor_updated_at and cursor_plan_id.
    // nextCursor is an object {updatedAt, planId} — router-confirmed S777.
    if (nextCursor?.updatedAt) {
      params.set('cursor_updated_at', nextCursor.updatedAt);
    }
    if (nextCursor?.planId != null) {
      params.set('cursor_plan_id', String(nextCursor.planId));
    }
    params.set('limit', '50');

    try {
      const response = await fetch(
        `/api/v1/plans/registry?${params.toString()}`,
        {
          credentials: 'include',
          signal: controller.signal,
        },
      );

      if (!mountedRef.current) return;
      if (controller.signal.aborted) return;

      if (!response.ok) {
        throw new Error(
          `Unable to load more plans (${response.status}).`,
        );
      }

      const payload = await response.json();

      if (!mountedRef.current) return;
      if (controller.signal.aborted) return;

      setPlans((current) => [
        ...current,
        ...(payload.plans || []),
      ]);

      // WHY: ep 1 router wraps cursor in pagination{nextCursor{updatedAt,planId}}.
      // Router-confirmed S777.
      setNextCursor(
        payload.pagination?.nextCursor ?? null,
      );
    } catch (requestError) {
      if (!mountedRef.current) return;

      if (requestError.name !== 'AbortError') {
        setError(
          requestError.message ||
            'Unable to load more compensation plans.',
        );
      }
    } finally {
      if (!mountedRef.current) return;
      if (controller.signal.aborted) return;

      setLoadingMore(false);
    }
  }

  async function handleSaveCurrentView(label) {
    try {
      const response = await fetch(
        '/api/v1/plans/registry/saved-views',
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            label,
            filters,
          }),
        },
      );

      if (!mountedRef.current) return;

      if (!response.ok) {
        throw new Error(
          `Unable to save view (${response.status}).`,
        );
      }

      await response.json();

      if (!mountedRef.current) return;

      await fetchSavedViews();

      if (!mountedRef.current) return;
    } catch (requestError) {
      if (!mountedRef.current) return;

      setError(
        requestError.message || 'Unable to save view.',
      );
    }
  }

  async function handleDeleteSavedView(viewId) {
    try {
      const response = await fetch(
        `/api/v1/plans/registry/saved-views/${encodeURIComponent(
          viewId,
        )}`,
        {
          method: 'DELETE',
          credentials: 'include',
        },
      );

      if (!mountedRef.current) return;

      if (!response.ok) {
        throw new Error(
          `Unable to delete saved view (${response.status}).`,
        );
      }

      await response.text();

      if (!mountedRef.current) return;

      await fetchSavedViews();

      if (!mountedRef.current) return;
    } catch (requestError) {
      if (!mountedRef.current) return;

      setError(
        requestError.message ||
          'Unable to delete saved view.',
      );
    }
  }

  function handleSelectSavedView(view) {
    setFilters({
      ...DEFAULT_FILTERS,
      ...view.filters,
      planStatus: [...(view.filters.planStatus || [])],
      temporalRelationship: [
        ...(view.filters.temporalRelationship || []),
      ],
      roleIds: [...(view.filters.roleIds || [])],
      segmentIds: [...(view.filters.segmentIds || [])],
      buIds: [...(view.filters.buIds || [])],
      primarySpine: [
        ...(view.filters.primarySpine || []),
      ],
      attentionLevel: [
        ...(view.filters.attentionLevel || []),
      ],
      sourceType: [...(view.filters.sourceType || [])],
    });

    setNextCursor(null);
  }

  // S800: remainingCount derived from totalCount (main response) not summary endpoint.
  const remainingCount = useMemo(() => {
    if (totalCount === null) return null;
    return Math.max(totalCount - plans.length, 0);
  }, [totalCount, plans.length]);

  // S802: full summary shape for PlanAttentionStrip — OI-S801-ATTENTION-STRIP-SUBCOUNTS closed.
  // Merge totalCount with registrySummary so PlanAttentionStrip receives all 7 subcounts.
  // registrySummary already contains totalCount from the router but we spread defensively.
  const summary = totalCount !== null
    ? { totalCount, ...(registrySummary ?? {}) }
    : null;

  const filtersActive = useMemo(
    () => hasRegistryFilters(filters),
    [filters],
  );

  const effectiveFiscalPeriodId =
    filters.fiscalPeriodId ?? fiscalPeriodId;

  // Group plans by segmentName preserving server sort order within each group.
  // Segment order follows first-appearance in the server response so the
  // sort_by/sort_dir the user chose still applies within each segment group.
  const segmentGroups = React.useMemo(() => {
    const order = [];
    const map = {};
    for (const plan of plans) {
      const seg = plan.segmentName || 'Other';
      if (!map[seg]) {
        map[seg] = [];
        order.push(seg);
      }
      map[seg].push(plan);
    }
    return order.map((seg) => ({ segmentName: seg, plans: map[seg] }));
  }, [plans]);

  return (
    <section
      className={`space-y-3 ${className}`}
      aria-label="Compensation plan registry"
    >
      <PlanRegistryToolbar
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        savedViews={savedViews}
        onSaveCurrentView={handleSaveCurrentView}
        onSelectSavedView={handleSelectSavedView}
        onDeleteSavedView={handleDeleteSavedView}
        onNewPlan={() => setShowCreationSheet(true)}
        fiscalPeriods={fiscalPeriods}
        roles={roles}
        segments={segments}
        loading={loading}
      />

      <PlanAttentionStrip
        summary={summary}
        loading={loading && !summary}
        onFilterApply={handleFilterChange}
      />

      {error && (
        <div
          role="alert"
          className="modifier-manager-inline-error flex items-center justify-between gap-3"
        >
          <span>{error}</span>

          <button
            type="button"
            className="text-button"
            onClick={() => fetchRegistry(filters)}
          >
            Retry
          </button>
        </div>
      )}

      <div
        role="region"
        aria-label="Compensation plans"
        aria-busy={loading}
        className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]"
      >
        <div
          className="sr-only"
          aria-live="polite"
        >
          {totalCount !== null
            ? `${totalCount} plans match the current registry filters.`
            : ''}
        </div>

        {loading && plans.length === 0 ? (
          <div
            className="space-y-2 p-4"
            aria-label="Loading compensation plans"
          >
            {[0, 1, 2, 3, 4].map((index) => (
              <div
                key={index}
                className="flex min-w-[1100px] items-center gap-4 py-3"
              >
                <div className="flex-1">
                  <div className="skeleton h-4 w-52 rounded" />
                  <div className="skeleton mt-2 h-3 w-28 rounded" />
                </div>
                <div className="skeleton h-6 w-[160px] rounded-full" />
                <div className="skeleton h-8 w-[150px] rounded" />
                <div className="skeleton h-8 w-[130px] rounded" />
                <div className="skeleton h-8 w-[150px] rounded" />
                <div className="skeleton h-8 w-[90px] rounded" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {segmentGroups.map((group, idx) => (
              <PlanSegmentGroup
                key={group.segmentName}
                segmentName={group.segmentName}
                plans={group.plans}
                selectedPlanId={selectedPlanId}
                defaultExpanded={idx === 0 || group.plans.some((p) => p.planId === selectedPlanId)}
                onOpen={handlePlanOpen}
                onSaveAs={handleSaveAs}
                onWithdraw={handleWithdraw}
                onDiscard={handleDiscard}
              />
            ))}

            {!loading &&
              plans.length === 0 &&
              !filtersActive && (
                <div className="px-6 py-12 text-center">
                  <div className="text-[13px] font-semibold text-[rgb(var(--color-text))]">
                    No plans found.
                  </div>

                  <p className="mt-1 text-[12px] text-[rgb(var(--color-text-muted))]">
                    Create your first plan.
                  </p>

                  <button
                    type="button"
                    className="primary-button mt-4"
                    onClick={() =>
                      setShowCreationSheet(true)
                    }
                  >
                    + New Plan
                  </button>
                </div>
              )}

            {!loading &&
              plans.length === 0 &&
              filtersActive && (
                <div className="px-6 py-12 text-center">
                  <div className="text-[13px] font-semibold text-[rgb(var(--color-text))]">
                    No plans match your current filters.
                  </div>

                  <button
                    type="button"
                    className="secondary-button mt-4"
                    onClick={handleClearFilters}
                  >
                    Clear filters
                  </button>
                </div>
              )}
          </>
        )}
      </div>

      {plans.length > 0 && (
        <div className="flex flex-col items-center py-2 gap-2">
          {/* S800: sentinel div — IntersectionObserver target for auto-load.
              Renders invisibly at the bottom of the list. When it enters the
              viewport the observer fires handleLoadMore automatically.
              UI-TABLE-01: seamless scroll pagination, no manual button. */}
          {nextCursor && (
            <div
              ref={sentinelRef}
              aria-hidden="true"
              className="h-4 w-full"
            />
          )}

          {loadingMore && (
            <div className="text-[11px] text-[rgb(var(--color-text-muted))]">
              Loading…
            </div>
          )}

          {/* Accessible fallback: keyboard / screen-reader users who cannot
              trigger intersection observer get an explicit button. Visually
              hidden until focused so it does not compete with the auto-load. */}
          {nextCursor && !loadingMore && (
            <button
              type="button"
              className={[
                // Visually hidden until focused — keyboard/screen-reader fallback
                // for users who cannot trigger IntersectionObserver scroll.
                // On focus renders as secondary-button equivalent (index.css line 677)
                // using inline Tailwind utilities — focus: variants work on utilities,
                // not on custom CSS classes like .secondary-button.
                'sr-only',
                'focus:not-sr-only',
                'focus:inline-flex focus:items-center focus:gap-1.5',
                'focus:px-3.5 focus:py-1.5 focus:rounded-lg',
                'focus:border focus:border-[rgb(var(--color-border))]',
                'focus:bg-[rgb(var(--color-surface))]',
                'focus:text-[rgb(var(--color-text))]',
                'focus:text-[12px] focus:font-medium focus:whitespace-nowrap',
                'focus:cursor-pointer',
              ].join(' ')}
              aria-label={
                remainingCount !== null
                  ? `Load more plans, ${remainingCount} remaining`
                  : 'Load more plans'
              }
              onClick={handleLoadMore}
            >
              {remainingCount !== null
                ? `Load more (${remainingCount} remaining)`
                : 'Load more'}
            </button>
          )}

          {!nextCursor && !loading && (
            <div className="text-[11px] text-[rgb(var(--color-text-muted))]">
              End of plan list
            </div>
          )}
        </div>
      )}

      <NewPlanCreationSheet
        open={showCreationSheet}
        fiscalPeriodId={effectiveFiscalPeriodId}
        currentUser={currentUser}
        roles={roles}
        segments={segments}
        onCancel={() => setShowCreationSheet(false)}
        onCreated={(planId) => {
          setShowCreationSheet(false);
          handlePlanOpen(planId);
        }}
      />
    </section>
  );
}
